﻿<template>
    <img :src="url" :alt="alt" @load="onLoad" @error="onError" />
</template>

<script lang="ts">

import type {PropType} from "vue";

import {defineComponent, computed, toRefs} from "vue";

const hashedEmailAddress = async (emailAddress: string) => {
  const msgBuffer = new TextEncoder().encode(emailAddress?.trim().toLowerCase() || "");

  // Hash the message using SHA-256
  const hashBuffer = await crypto.subtle.digest('SHA-256', msgBuffer);

  // Convert the ArrayBuffer to a hex string
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map(b => ('00' + b.toString(16)).slice(-2)).join('');
}

export type DefaultGravatarImage = "404" | "mp" | "identicon" | "monsterid" | "wavatar" | "retro" | "robohash" | "blank";

export default defineComponent({
    name: "AunoaGravatar",
    props: {
        emailAddress: {
            type: String,
            default: ""
        },
        hash: {
            type: String,
            default: ""
        },
        size: {
            type: Number,
            default: 64
        },
        defaultImg: {
            type: String as PropType<DefaultGravatarImage>,
            default: "blank"
        },
        rating: {
            type: String,
            default: "g"
        },
        alt: {
            type: String,
            default: "Avatar"
        },
        protocol: {
            type: String,
            default: ""
        }
    },
    emits: ["load", "error"],
    async setup(props, {emit}) {
      
        const {protocol, emailAddress, hash, size, defaultImg, rating} = toRefs(props);
        const prot = computed(() =>
            protocol.value.slice(-1) === ':'
                ? protocol.value
                : `${protocol.value}:`);
        const emailHash =hash.value || await hashedEmailAddress(emailAddress.value);
        const url = computed(() =>
            [
                `${prot.value === ':' ? '' : prot.value}//www.gravatar.com/avatar/`,
                emailHash,
                `?s=${size.value}`,
                `&d=${defaultImg.value}`,
                `&r=${rating.value}`
            ].join(""));

        const onLoad = (...args: any[]) => emit("load", ...args);
        const onError = (...args: any[]) => emit("error", ...args);

        return {
            url,
            onLoad,
            onError
        }
    }
});

</script>

